import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { TID } from "@/lib/testIds";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const LEGEND = [
  ["available", "Available"], ["occupied", "Occupied"], ["owner_reserved", "Owner"],
  ["leased", "Leased"], ["daily_rental", "Daily Rental"], ["guest", "Guest"],
  ["staff", "Staff"], ["ev", "EV"], ["maintenance", "Maint"],
];

export default function ParkingMap() {
  const [towers, setTowers] = useState([]);
  const [floors, setFloors] = useState([]);
  const [zones, setZones] = useState([]);
  const [slots, setSlots] = useState([]);
  const [towerId, setTowerId] = useState("all");
  const [floorId, setFloorId] = useState("all");

  useEffect(() => {
    api.get("/towers").then(r => setTowers(r.data));
    api.get("/floors").then(r => setFloors(r.data));
    api.get("/zones").then(r => setZones(r.data));
    api.get("/slots").then(r => setSlots(r.data));
  }, []);

  const filtered = useMemo(() => {
    return slots.filter(s =>
      (towerId === "all" || s.tower_id === towerId) &&
      (floorId === "all" || s.floor_id === floorId)
    );
  }, [slots, towerId, floorId]);

  const byZone = useMemo(() => {
    const m = {};
    for (const s of filtered) {
      m[s.zone_id] = m[s.zone_id] || [];
      m[s.zone_id].push(s);
    }
    return m;
  }, [filtered]);

  return (
    <div className="space-y-6">
      <header>
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500">Parking Map</p>
        <h1 className="font-heading text-4xl font-black tracking-tighter mt-1">Where things are.</h1>
      </header>

      <Card className="bg-[#141414] border-zinc-800 p-4 flex flex-wrap items-center gap-3">
        <Select value={towerId} onValueChange={(v) => { setTowerId(v); setFloorId("all"); }}>
          <SelectTrigger className="w-44 bg-[#0F0F10] border-zinc-800" data-testid="map-tower-filter">
            <SelectValue placeholder="All Towers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Towers</SelectItem>
            {towers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={floorId} onValueChange={setFloorId}>
          <SelectTrigger className="w-44 bg-[#0F0F10] border-zinc-800" data-testid="map-floor-filter">
            <SelectValue placeholder="All Floors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Floors</SelectItem>
            {floors
              .filter(f => towerId === "all" || f.tower_id === towerId)
              .map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex flex-wrap gap-2 items-center">
          {LEGEND.map(([k, l]) => (
            <div key={k} className="flex items-center gap-1.5">
              <div className={`h-3 w-3 rounded-sm slot-${k}`} />
              <span className="text-[11px] font-mono text-zinc-400">{l}</span>
            </div>
          ))}
        </div>
      </Card>

      <div className="space-y-6">
        {zones
          .filter(z => filtered.some(s => s.zone_id === z.id))
          .map(z => {
            const tower = towers.find(t => t.id === z.tower_id);
            const floor = floors.find(f => f.id === z.floor_id);
            return (
              <Card key={z.id} className="bg-[#141414] border-zinc-800 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500">
                      {tower?.name} · {floor?.name}
                    </p>
                    <h3 className="font-heading text-xl font-bold tracking-tight">{z.name}</h3>
                  </div>
                  <span className="text-xs font-mono text-zinc-500">
                    {(byZone[z.id] || []).length} slots
                  </span>
                </div>
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
                  {(byZone[z.id] || []).map(s => (
                    <div
                      key={s.id}
                      data-testid={TID.slot(s.slot_number)}
                      className={`slot-${s.status} h-16 rounded-md flex flex-col items-center justify-center font-mono text-xs font-semibold transition-transform hover:scale-105 cursor-pointer border border-black/20`}
                      title={`${s.slot_number} · ${s.status} · ${s.slot_type}`}
                    >
                      <span>{s.slot_number}</span>
                      <span className="text-[9px] opacity-80 uppercase tracking-wide">{s.slot_type}</span>
                    </div>
                  ))}
                </div>
              </Card>
            );
          })}
      </div>
    </div>
  );
}
